import { useTheme } from '@mui/material'
import NodeGeneralProperties from './NodeGeneralProperties'
import { NIGHT_JOB_CONTEXT_OFF } from './jobNightCharacter'
import JobTaskTable from './JobTaskTable'

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node}Node Node object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').TaskReference} TaskReference TaskReference object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference} JobReference JobReference object
 * @typedef {import('../../generic/utilities/objectStructures/globalStructures').IdCodeName} IdCodeName IdCodeName object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').ProgressCalculationType} ProgressCalculationType ProgressCalculationType object
 */

/**
 * The job panel of the graph: the general properties of the node, and its task table underneath.
 *
 * The table itself lives in `JobTaskTable` since story #150 (D16), so the process tree can mount the
 * very same one in its side panel and in its job dialog. This component keeps the general properties
 * and threads the task props through — nothing about tasks is decided here any more.
 *
 * @param {Object} props
 * @param {Node} props.node - Current node
 * @param {boolean} props.editing - Is editing enabled
 * @param {function(TaskReference[])} props.updateTaskList - Function to update the task list
 * @param {function(JobReference)} props.updateJobReference - Function to update the node
 * @param {function(boolean)} props.onEditingRowChanged - Reports whether its table holds an open row
 * @param {string} props.workflowName - Name of the workflow
 * @param {ProgressCalculationType[]} props.progressCalculationTypes - Progress calculation types array
 * @param {Object} props.partReference - PartReference object
 * @param {function(string)} props.getTempUUID function to get Temporally assigned UUID for a given UUID
 * @param {function(string)} props.setTempUUID function to set new temporal UUID
 * @param {import('../../generic/utilities/objectStructures/globalStructures').IdName[]} props.indexValues
 * @param {boolean} props.workflowUseIndex
 * @param {boolean} [props.showMachineTime] Story 208: SHOW_TASK_MACHINE_TIME, handed to the task table
 * @param {boolean} [props.featureGroupRevisionControlled] Story 186: CONTROLLED feature group revisions (S70), handed to the task table
 * @param {Object<string, object>} [props.featureGroupBaselines] Story 186: persisted copy of each feature group by id
 * @param {boolean} [props.fabricationOrderOperable] Whether the FO admits execution. Threaded through
 *   only to reach `NodeGeneralProperties`, which needs it as one of the three gating factors of the
 *   Extern checkbox (RN21). Defaults to `true` for the template/subworkflow contexts that have no FO.
 * @param {{enabled: boolean, nightJobNodeIds: string[]}} [props.nightJobContext] Story 183: threaded
 *   through to `NodeGeneralProperties`, where the "Night job" control lives.
 * @returns {JSX.Element}
 */
const JobTask = ({ node, linkedJobReference = null, canPublishOperation = false, workflowId = 0, onOperationPublish = null, operationLink = null, onOperationUpdate = null, onOperationDismiss = null, onOperationUnlink = null, canViewOperationLibrary = false, onOpenOperationDetail = null, sourceProcessCode = '', sourceProcessVersionNumber = null, technologyEditable = false, operationIdentity = {}, onOperationIdentityChange = null, editing, updateTaskList, updateJobReference, onEditingRowChanged, workflowName, progressCalculationTypes, partReference, getTempUUID, setTempUUID, indexValues, workflowUseIndex, showMachineTime = false, cascadeApprovalEnabled = false, featureGroupRevisionControlled = false, featureGroupBaselines = {}, fabricationOrderOperable = true, nightJobContext = NIGHT_JOB_CONTEXT_OFF }) => {
  const theme = useTheme()

  return (
    <>
      <div style={{ paddingBottom: '10px', backgroundColor: theme.palette.background.color_373636 }}>
        <NodeGeneralProperties
          jobReference={node?.jobReference}
          node={node}
          linkedJobReference={linkedJobReference}
          canPublishOperation={canPublishOperation}
          workflowId={workflowId}
          onOperationPublish={onOperationPublish}
          operationLink={operationLink}
          onOperationUpdate={onOperationUpdate}
          onOperationDismiss={onOperationDismiss}
          onOperationUnlink={onOperationUnlink}
          canViewOperationLibrary={canViewOperationLibrary}
          onOpenOperationDetail={onOpenOperationDetail}
          sourceProcessCode={sourceProcessCode}
          sourceProcessVersionNumber={sourceProcessVersionNumber}
          technologyEditable={technologyEditable}
          operationIdentity={operationIdentity}
          onOperationIdentityChange={onOperationIdentityChange}
          editing={editing}
          updateJobReference={updateJobReference}
          progressCalculationTypes={progressCalculationTypes}
          indexValues={indexValues}
          workflowUseIndex={workflowUseIndex}
          cascadeApprovalEnabled={cascadeApprovalEnabled}
          featureGroupRevisionControlled={featureGroupRevisionControlled}
          featureGroupBaselines={featureGroupBaselines}
          fabricationOrderOperable={fabricationOrderOperable}
          nightJobContext={nightJobContext}
        />
      </div>
      <JobTaskTable
        node={node}
        editing={editing}
        updateTaskList={updateTaskList}
        onEditingRowChanged={onEditingRowChanged}
        workflowName={workflowName}
        partReference={partReference}
        getTempUUID={getTempUUID}
        setTempUUID={setTempUUID}
        indexValues={indexValues}
        workflowUseIndex={workflowUseIndex}
        showMachineTime={showMachineTime}
        featureGroupRevisionControlled={featureGroupRevisionControlled}
        featureGroupBaselines={featureGroupBaselines}
      />
    </>
  )
}

export default JobTask
